import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ListChecks, Pencil, ChevronDown } from "lucide-react";
import { VariableInput } from "@/components/flow/properties/VariableInput";
import { cn } from "@/lib/utils";
import StepShell from "../StepShell";
import { usePracticeAreas, useQualificationQuestions } from "../useBriefing";

/**
 * Bloco 2 — Roteiro de triagem. Uma área por vez (accordion) para reduzir a
 * carga cognitiva: as demais ficam recolhidas mostrando só um resumo. Em cada
 * área o cliente aprova o roteiro padrão ou escolhe "Quero ajustar".
 */
export default function Bloco2Triagem({ briefing, autosave }) {
  const reduceMotion = useReducedMotion();
  const selectedAreas = briefing.areas?.selected || [];
  const { data: areasData } = usePracticeAreas();
  const { data: questionsData, isLoading } = useQualificationQuestions(selectedAreas);

  const areaLabel = (id) =>
    areasData?.areas?.find((a) => a.id === id)?.label || id;
  const byAreaQuestions = questionsData?.byArea || {};

  const [triage, setTriage] = useState(briefing.triage?.byArea || {});
  const [openArea, setOpenArea] = useState(selectedAreas[0] || null);

  function updateArea(areaId, patch) {
    const current = triage[areaId] || {
      questionIds: (byAreaQuestions[areaId] || []).map((q) => q.id),
      approved: true,
      adjustments: "",
    };
    const nextArea = { ...current, ...patch };
    const next = { ...triage, [areaId]: nextArea };
    setTriage(next);
    autosave({ triage: { byArea: next } });
  }

  if (selectedAreas.length === 0) {
    return (
      <StepShell title="Roteiro de triagem">
        <p className="text-sm text-muted-foreground">
          Selecione as áreas no passo anterior para ver os roteiros padrão.
        </p>
      </StepShell>
    );
  }

  return (
    <StepShell
      title="Roteiro de triagem"
      description="As perguntas-padrão que a IA fará para qualificar cada caso. Abra uma área por vez — aprove ou ajuste."
    >
      <div className="space-y-3">
        {selectedAreas.map((areaId) => {
          const questions = byAreaQuestions[areaId] || [];
          const state = triage[areaId] || { approved: true, adjustments: "" };
          const adjusting = state.approved === false;
          const isOpen = openArea === areaId;

          return (
            <div
              key={areaId}
              className={cn(
                "tw-morph-radius overflow-hidden rounded-xl border transition-colors",
                isOpen
                  ? "border-border-strong bg-surface-inset/50"
                  : "border-border bg-surface/30 hover:border-border-strong",
              )}
            >
              {/* Header */}
              <button
                type="button"
                onClick={() => setOpenArea(isOpen ? null : areaId)}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                aria-expanded={isOpen}
              >
                <span className="flex items-center gap-3">
                  <span className="text-sm font-medium text-foreground">
                    {areaLabel(areaId)}
                  </span>
                  {!isOpen && (
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[11px] font-medium",
                        adjusting
                          ? "bg-[rgb(var(--cyan)/0.12)] text-[rgb(var(--cyan))]"
                          : "bg-surface-active/60 text-muted-foreground",
                      )}
                    >
                      {adjusting
                        ? "Ajustado"
                        : `${questions.length} pergunta${questions.length === 1 ? "" : "s"} · padrão`}
                    </span>
                  )}
                </span>
                <motion.span
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: reduceMotion ? 0 : 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="text-muted-foreground"
                >
                  <ChevronDown className="h-4 w-4" />
                </motion.span>
              </button>

              {/* Body */}
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key="body"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: reduceMotion ? 0 : 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5">
                      <div className="mb-4 flex flex-wrap gap-2">
                        <ModeButton
                          active={!adjusting}
                          onClick={() => updateArea(areaId, { approved: true })}
                          icon={ListChecks}
                        >
                          Usar roteiro padrão
                        </ModeButton>
                        <ModeButton
                          active={adjusting}
                          onClick={() => updateArea(areaId, { approved: false })}
                          icon={Pencil}
                        >
                          Quero ajustar
                        </ModeButton>
                      </div>

                      {isLoading ? (
                        <p className="text-sm text-muted-foreground">Carregando perguntas…</p>
                      ) : (
                        <ul className="space-y-2">
                          {questions.map((q) => (
                            <li key={q.id} className="flex gap-2.5 text-sm leading-relaxed text-foreground/80">
                              <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[rgb(var(--cyan))]" />
                              {q.text}
                            </li>
                          ))}
                          {questions.length === 0 && (
                            <li className="text-sm text-muted-foreground">
                              Sem roteiro padrão para esta área — descreva no campo de ajuste.
                            </li>
                          )}
                        </ul>
                      )}

                      {adjusting && (
                        <VariableInput
                          multiline
                          variables={[]}
                          className="mt-4"
                          placeholder="Descreva as perguntas ou ajustes que quer nesta área…"
                          value={state.adjustments || ""}
                          onChange={(e) => updateArea(areaId, { adjustments: e.target.value })}
                        />
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </StepShell>
  );
}

function ModeButton({ active, onClick, icon: Icon, children }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.96 }}
      className={cn(
        "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors",
        active
          ? "border-cyan-500/60 bg-cyan-500/10 text-cyan-600 dark:text-cyan-400"
          : "border-border bg-surface text-muted-foreground hover:border-border-strong",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </motion.button>
  );
}
